import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import type { MutableRefObject, ReactNode } from 'react';
import { isExpandAction } from './actions';
import { DataTableBodyRow } from './BodyRow';
import { DataTableFilterDropdown } from './FilterDropdown';
import { DataTableSortHeader } from './SortHeader';
import { DataTableActions, DataTableFilters } from './Controls';
import { DataTableExpandToggle, DataTableSearch, DataTableToolbar } from './Toolbar';
import { DataTablePaginationBar } from './PaginationBar';
import { DataTableLoadedCount } from './LoadedCount';
import { DataTableRowCount } from './RowCount';
import { LoadMoreRow } from './LoadMoreRow';
import { DataTableModeToggle } from './ModeToggle';
import { buildGroupedRows } from './groupedRows';
import { SelectionBar } from './SelectionControls';
import { useSelectionColumn } from './useSelectionColumn';
import type { DataTableColumn, DataTableProps, DataTableSort } from './types';
import { useAutoColumnWidthSync } from './useAutoColumnWidthSync';
import { useOverlayScrollbar } from './useOverlayScrollbar';
import { cellClasses, cx, HEADER_CELL_BASE, ROW_BORDER, visibleColumns } from './utils';

export const DATA_TABLE_ROW_HEIGHT_CLASS = 'h-14';
const DEFAULT_HEADER_ROW_HEIGHT = 'h-10';
const DEFAULT_SKELETON_ROWS = 8;
const HEADER_SHADOW = 'shadow-[0px_1px_2px_rgba(0,0,0,0.15)]';
const CARD_SHADOW = 'shadow-[0px_1px_2px_rgba(0,0,0,0.15)]';

/**
 * `border-collapse: separate` with zero spacing, on BOTH tables so their
 * geometry matches. The separated model ignores borders on `<tr>`, so every
 * row rule is painted on the cells instead (`ROW_BORDER`) - that cell-painted
 * scheme now depends on this model. (`separate` dates from the sticky-header
 * era, when a sticky `<th>`'s borders would detach under `collapse`; the
 * header has since moved out of the scroll region entirely.)
 *
 * Deliberately `as const` rather than annotated `CSSProperties`: shared-ui has
 * its own nested `csstype`, so an explicit annotation would bind these
 * properties to *this* package's copy and fail to unify with a consuming app's.
 */
const TABLE_STYLE = { borderCollapse: 'separate', borderSpacing: 0 } as const;

/**
 * The overlay scrollbar's thumb (`useOverlayScrollbar`). Painted on top of
 * the rows, absolutely positioned in the scroll region's `relative` wrapper.
 * Sizing and vertical position are written straight to the element's style by
 * the hook, once per scroll frame; these classes carry everything else. The
 * palette deliberately mirrors the `scrollbar-subtle` utility this replaced:
 * 6px wide, gray-300 thumb, gray-400 on hover, fully rounded.
 */
const SCROLLBAR_THUMB_BASE =
    'absolute right-0.5 top-0 z-10 w-1.5 touch-none rounded-full bg-gray-300 hover:bg-gray-400 transition-opacity duration-200';

/**
 * Left edge of the expanded overlay. A CSS custom property, not a hardcoded
 * number: how wide the app's nav sidebar is (files-viewer: 220px expanded /
 * 60px collapsed) is an app-level concern this package must not know. The app
 * sets `--odo-table-overlay-left` on any ancestor; the fallback is a
 * full-bleed overlay.
 */
const DEFAULT_OVERLAY_LEFT = 'var(--odo-table-overlay-left, 0px)';

function ariaSortFor<T, K extends string>(
    column: DataTableColumn<T, K>,
    sort: DataTableSort<K> | undefined
): 'ascending' | 'descending' | 'none' | undefined {
    if (!column.sortable || !sort) {
        return undefined;
    }
    if (sort.by !== column.sortKey) {
        return 'none';
    }
    return sort.order === 'asc' ? 'ascending' : 'descending';
}

interface HeaderRowProps<T, K extends string> {
    columns: ReadonlyArray<DataTableColumn<T, K>>;
    sort?: DataTableSort<K>;
    className?: string;
}

/**
 * The VISIBLE header row. It lives in a `role="presentation"` table above the
 * scroll region, so these cells carry no table semantics on purpose - no
 * `scope`, no `aria-sort`; `A11yHeaderRow` inside the body table is where
 * assistive tech reads the column structure from. The sort buttons, however,
 * are real: `role="presentation"` strips the *table* semantics only, so the
 * buttons stay in the accessibility tree, keyboard-reachable in DOM order just
 * before the scroll region, and announce their own sorted state (see
 * `DataTableSortHeader`).
 */
function HeaderRow<T, K extends string>({ columns, sort, className }: HeaderRowProps<T, K>) {
    return (
        <tr className={className ?? DEFAULT_HEADER_ROW_HEIGHT}>
            {columns.map((column, i) => {
                const header =
                    column.headerNode !== undefined ? (
                        column.headerNode
                    ) : column.sortable && sort ? (
                        <DataTableSortHeader
                            label={column.label}
                            column={column.sortKey}
                            sortBy={sort.by}
                            sortOrder={sort.order}
                            onSort={sort.onSort}
                            className={
                                column.align === 'right'
                                    ? 'ml-auto'
                                    : column.align === 'center'
                                      ? 'mx-auto'
                                      : undefined
                            }
                        />
                    ) : column.label ? (
                        // Truncates at narrow widths instead of squashing neighbours.
                        <span className="block truncate">{column.label}</span>
                    ) : (
                        column.label
                    );
                return (
                    <th
                        key={column.key}
                        className={cx(
                            HEADER_CELL_BASE,
                            HEADER_SHADOW,
                            ROW_BORDER,
                            'overflow-hidden',
                            column.align === 'right' && 'text-right',
                            column.align === 'center' && 'text-center',
                            i === 0 && 'rounded-tl-xl',
                            i === columns.length - 1 && 'rounded-tr-xl',
                            column.headerClassName
                        )}
                    >
                        {(column.align === 'right' || column.align === 'center') &&
                        column.sortable &&
                        sort ? (
                            <div
                                className={cx(
                                    'flex',
                                    column.align === 'right' ? 'justify-end' : 'justify-center'
                                )}
                            >
                                {header}
                            </div>
                        ) : (
                            header
                        )}
                    </th>
                );
            })}
        </tr>
    );
}

interface A11yHeaderRowProps<T, K extends string> {
    columns: ReadonlyArray<DataTableColumn<T, K>>;
    sort?: DataTableSort<K>;
}

/**
 * The SEMANTIC header row, inside the body table. The visible header lives in
 * a separate presentational table above the scroll region (so the vertical
 * scrollbar spans the rows only), which would leave this table headerless for
 * screen readers - this row is what keeps it a real table: each `<td>`
 * associates with these `<th scope="col">`s, and `aria-sort` lives here, on
 * `<th>`s assistive tech actually maps to columns.
 *
 * Visually it must contribute nothing: the labels are `sr-only` (absolutely
 * positioned, so zero content height) and the cells carry no padding and no
 * border, collapsing the row to zero height. It is NOT `aria-hidden` and not
 * `display: none` - either would defeat its entire purpose.
 */
function A11yHeaderRow<T, K extends string>({ columns, sort }: A11yHeaderRowProps<T, K>) {
    return (
        <tr>
            {columns.map((column) => (
                <th
                    key={column.key}
                    scope="col"
                    aria-sort={ariaSortFor(column, sort)}
                    className="p-0"
                >
                    <span className="sr-only">{column.label}</span>
                </th>
            ))}
        </tr>
    );
}

interface SkeletonBodyProps<T, K extends string> {
    columns: ReadonlyArray<DataTableColumn<T, K>>;
    rowCount: number;
    rowHeightClassName: string;
}

function SkeletonBody<T, K extends string>({
    columns,
    rowCount,
    rowHeightClassName
}: SkeletonBodyProps<T, K>) {
    return (
        <>
            {Array.from({ length: rowCount }).map((_, rowIndex) => (
                <tr key={rowIndex} className={cx(rowHeightClassName)}>
                    {columns.map((column) => (
                        <td key={column.key} className={cx(cellClasses(column), ROW_BORDER)}>
                            <div className="h-3.5 w-3/4 rounded bg-gray-200 animate-pulse" />
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}

interface FullWidthRowProps {
    colSpan: number;
    className: string;
    children: ReactNode;
}

function FullWidthRow({ colSpan, className, children }: FullWidthRowProps) {
    return (
        <tr>
            <td colSpan={colSpan} className={className}>
                {children}
            </td>
        </tr>
    );
}

/**
 * Server-side driven, semantically-correct data table.
 *
 * The component renders controls and calls back; it holds no sort, filter or
 * page state, performs no fetching, and knows nothing about the query layer.
 * `useDataTable` is where that state lives when you want it held for you.
 *
 * Two modes, both server-driven:
 * - `mode="paged"` (default) - `page`/`limit`, pagination footer;
 * - `mode="infinite"` - a visible "Load more" button (and a sentinel) call
 *   `onLoadMore`, and the app accumulates the chunks it fetched and hands the
 *   whole list back as `rows`; the footer shows a loaded-vs-total count in
 *   place of a pager. Nothing is sliced client-side in either mode.
 *
 * The footer always renders, in every mode: a pager (`pagination` supplied,
 * `'paged'`), a loaded-vs-total count (`'infinite'`), or - lacking both - a
 * plain row count, so a table with no paging at all (`PracticeUsersTable`)
 * never shows a dead pager. `onModeChange`'s toggle sits in this same footer,
 * next to whichever of the three is showing.
 *
 * Layout invariants it guarantees for you:
 * - the header row lives in its own presentational table ABOVE the scroll
 *   region, so the vertical scrollbar spans the rows only - it starts below
 *   the header and ends above the footer - and the header can never scroll
 *   away (no sticky positioning involved). `A11yHeaderRow` inside the body
 *   table keeps the split invisible to assistive tech;
 * - both tables render a `<colgroup>` generated from the same visible-columns
 *   list (`tableLayout="auto"` pixel-syncs it instead - see
 *   `useAutoColumnWidthSync`), and the empty/loading `colSpan` derives from
 *   that same list, so none of the three can drift;
 * - the vertical scrollbar is a hand-rolled overlay (`useOverlayScrollbar`):
 *   the native bar is hidden (`scrollbar-hide`) and a thumb paints on top of
 *   the rows - fading in on scroll or pointer approach, fading out when idle,
 *   draggable while visible. It costs zero layout space, so no gutter is
 *   reserved anywhere and the header and body tables are identical in width
 *   by construction, whether or not the rows overflow;
 * - the scroll region clips the X axis outright (`overflow-x: hidden`): a
 *   horizontal scrollbar is never rendered, and stray cell overflow clips
 *   instead of widening the region. Cell popovers (assignee picker, row
 *   menu) stay within the region's x-bounds at realistic widths, so the clip
 *   never amputates them;
 * - `table-layout: fixed` is paired with an explicit `width: 100%`, without
 *   which the browser ignores it;
 * - clipping is opt-in per column (`truncate`), never blanket
 *   `overflow-hidden` on every cell - cells must stay able to host live
 *   interactive React such as an assignee dropdown or a row menu, which a
 *   blanket clip would cut off regardless of z-index;
 * - the scroll container is a named, focusable `region` so keyboard users can
 *   actually scroll it.
 *
 * Body rows are memoized (`DataTableBodyRow`): a parent re-render that leaves
 * the rows' inputs untouched - a keystroke in a co-located search box, say -
 * re-renders no cells, which is what keeps `mode="infinite"`'s accumulated
 * rows cheap. The one input this component cannot stabilize is the caller's
 * `columns` array: **construct it with `useMemo`** (keyed on whatever state
 * the cell renderers close over), or every parent render re-renders every
 * row. `rowAction` and `rowClassName` need no such care - the memo never sees
 * their identity, only stable or value-compared derivations of them.
 *
 * Two things deliberately *stay outside* this component's surface:
 * - **column presets** (`textColumn`, `statusColumn`, …) are factories that
 *   return a `DataTableColumn<T, K>`. They add no props here; the component still
 *   consumes a flat column list, and hand-written `render` columns mix into
 *   the same array untouched. That is what keeps this from turning into a
 *   config-driven god object;
 * - **filter and search state**. `search`/`filters` render controls and call
 *   back. No state, no debounce, no fetch - all three are the app's job.
 */
export function DataTable<T, K extends string = string>({
    columns,
    rows,
    getRowKey,
    label,
    sort,
    pagination,
    mode = 'paged',
    onLoadMore,
    hasMore = false,
    isLoadingMore = false,
    loadedCount,
    loadMoreLabel = 'Load more',
    onModeChange,
    rowAction,
    selection,
    rowAccent,
    grouping,
    cardHeader,
    tableLayout = 'fixed',
    rowHeightClassName = DATA_TABLE_ROW_HEIGHT_CLASS,
    rowClassName,
    headerRowClassName,
    isLoading = false,
    loadingRows,
    skeletonRowCount = DEFAULT_SKELETON_ROWS,
    emptyState,
    emptyStateCellClassName = 'px-3 py-12 text-center text-sm text-gray-500',
    search,
    filters,
    actions,
    toolbar,
    expansion,
    overlayLeft,
    className,
    cardClassName,
    scrollClassName,
    refs
}: DataTableProps<T, K>) {
    // Memoized so the rows' memo (`DataTableBodyRow`) sees one array identity
    // per `columns` identity, not a fresh filter result per render.
    const baseShown = useMemo(() => visibleColumns(columns), [columns]);

    // Checkbox column + selected state, memo-safe - see useSelectionColumn.
    const { selectionColumn, isRowSelected, toggleSelection, selectedTotal } = useSelectionColumn<
        T,
        K
    >(selection, rows, getRowKey);
    const shown = useMemo(
        () => (selectionColumn ? [selectionColumn, ...baseShown] : baseShown),
        [selectionColumn, baseShown]
    );
    const isFixed = tableLayout !== 'auto';
    const isInfinite = mode === 'infinite';

    // The split header/body tables must agree on column widths. Fixed layout
    // gets that structurally (identical <colgroup>s); auto layout needs the
    // body's measured widths mirrored onto the header table.
    const headerTableRef = useRef<HTMLTableElement>(null);
    const bodyTableRef = useRef<HTMLTableElement>(null);
    useAutoColumnWidthSync(!isFixed, headerTableRef, bodyTableRef, shown.length, rows);

    // `rowAction` is written inline by every caller; the latest-ref keeps the
    // memoized rows on one permanently-stable callback with fresh behavior.
    const latestRowAction = useRef(rowAction);
    useLayoutEffect(() => {
        latestRowAction.current = rowAction;
    });
    // Selection toggle wins while enabled; rowAction resumes when it isn't.
    const activateRow = useCallback(
        (row: T, index: number) => {
            if (toggleSelection(row, index)) return;
            latestRowAction.current?.onActivate(row, index);
        },
        [toggleSelection]
    );

    // The overlay scrollbar needs the scroll region element, and so does the
    // caller (`refs.scroll`, viewport-fit page sizing). One callback ref
    // feeds both. The cast is safe: `DataTableRefs` are created with
    // `useRef`, whose `.current` is writable at runtime - React 18's
    // `RefObject` type just doesn't admit it.
    const overlay = useOverlayScrollbar();
    const { setScrollElement } = overlay;
    const externalScrollRef = refs?.scroll;
    const setScrollRegion = useCallback(
        (node: HTMLDivElement | null) => {
            setScrollElement(node);
            if (externalScrollRef) {
                (externalScrollRef as MutableRefObject<HTMLDivElement | null>).current = node;
            }
        },
        [setScrollElement, externalScrollRef]
    );

    let body: ReactNode;
    if (isLoading) {
        body = loadingRows ?? (
            <SkeletonBody
                columns={shown}
                rowCount={skeletonRowCount}
                rowHeightClassName={rowHeightClassName}
            />
        );
    } else if (rows.length === 0) {
        body = emptyState ? (
            <FullWidthRow colSpan={shown.length} className={emptyStateCellClassName}>
                {emptyState}
            </FullWidthRow>
        ) : null;
    } else {
        // Everything identity-unstable is resolved to a value HERE, outside
        // the memo boundary: `rowClassName` and `getLabel` run per render
        // (they are cheap; the cells are not) and their string results are
        // what the memo compares. See `DataTableBodyRow` for why this holds.
        const selecting = selection?.enabled === true;
        const rowNode = (row: T, index: number) => {
            const selectedClass =
                selecting && isRowSelected(row, index)
                    ? (selection.selectedRowClassName ?? 'bg-gray-100/80')
                    : undefined;
            return (
                <DataTableBodyRow
                    key={getRowKey(row, index)}
                    row={row}
                    index={index}
                    columns={shown}
                    rowHeightClassName={rowHeightClassName}
                    extraClassName={cx(rowClassName?.(row, index), selectedClass) || undefined}
                    actionLabel={rowAction?.getLabel?.(row)}
                    accentColor={rowAccent?.(row, index)}
                    actionMode={
                        selecting ? 'click' : rowAction ? (rowAction.mode ?? 'button') : undefined
                    }
                    onActivate={activateRow}
                />
            );
        };
        body = grouping
            ? buildGroupedRows(rows, grouping, shown.length, rowNode)
            : rows.map(rowNode);
    }

    // Toolbar composition, as documented on `DataTableProps.toolbar`: the
    // declarative props generate the row, and the slot - if any - drops into
    // it between the filters and the actions. With no declarative props the
    // slot is rendered verbatim and this component adds nothing of its own.
    //
    // An `expandAction` entry is only honoured when the `expansion` prop is
    // also set: without it the overlay the toggle drives can never render,
    // and a live toggle wired to nothing is worse than a missing one.
    const shownActions = expansion ? actions : actions?.filter((action) => !isExpandAction(action));
    const hasFilters = Boolean(filters && filters.length > 0);
    const hasActions = Boolean(shownActions && shownActions.length > 0);
    const generateToolbar = Boolean(search) || hasFilters || hasActions;
    const toolbarNode = generateToolbar ? (
        <DataTableToolbar
            start={search ? <DataTableSearch {...search} /> : undefined}
            end={
                <>
                    {filters && hasFilters && <DataTableFilters filters={filters} />}
                    {toolbar}
                    {shownActions && hasActions && <DataTableActions actions={shownActions} />}
                </>
            }
        />
    ) : (
        toolbar
    );

    // Footer content is picked by mode/`pagination`, never by both at once;
    // the mode toggle (if any) rides alongside whichever of the three shows.
    const modeToggle = onModeChange ? (
        <DataTableModeToggle mode={mode} onChange={onModeChange} />
    ) : undefined;
    const footer =
        !isInfinite && pagination ? (
            <DataTablePaginationBar {...pagination} trailing={modeToggle} />
        ) : isInfinite ? (
            <DataTableLoadedCount
                loaded={loadedCount ?? rows.length}
                total={pagination?.total}
                trailing={modeToggle}
            />
        ) : (
            <DataTableRowCount count={loadedCount ?? rows.length} trailing={modeToggle} />
        );

    const content = (
        <>
            {toolbarNode}
            <div
                ref={refs?.card}
                className={cx(
                    'flex-1 min-h-0 flex flex-col w-full bg-white border border-gray-200 rounded-xl overflow-hidden',
                    CARD_SHADOW,
                    cardClassName
                )}
            >
                {selection?.enabled && (
                    <SelectionBar
                        count={selectedTotal}
                        notice={selection.actionsNotice}
                        actions={selection.actions}
                    />
                )}
                {cardHeader}
                {/* The visible header, OUTSIDE the scroll region so the
                    overlay scrollbar below starts under it and the header can
                    never scroll away. Presentational: the body table's
                    A11yHeaderRow carries the semantics, but the sort buttons
                    in here are real and focusable - role="presentation"
                    strips table semantics only, which is exactly why this is
                    not aria-hidden. `overflow: hidden` clips stray header
                    overflow. */}
                <div className="shrink-0 overflow-hidden">
                    <table
                        ref={headerTableRef}
                        role="presentation"
                        style={TABLE_STYLE}
                        className="w-full table-fixed"
                    >
                        <colgroup>
                            {shown.map((column) => (
                                <col
                                    key={column.key}
                                    style={isFixed ? { width: column.width } : undefined}
                                />
                            ))}
                        </colgroup>
                        <thead ref={refs?.head}>
                            <HeaderRow columns={shown} sort={sort} className={headerRowClassName} />
                        </thead>
                    </table>
                </div>
                {/* The rows' scroll region. It is focusable and named so
                    keyboard-only users can scroll it (WCAG 2.1.1). Only the Y
                    axis ever scrolls: X is clipped outright, so no horizontal
                    scrollbar can appear no matter what a cell does. The
                    native vertical bar is hidden (`scrollbar-hide`) and
                    replaced by the overlay thumb rendered as a sibling below,
                    inside this `relative` wrapper - painted over the rows, it
                    costs no layout space, so no gutter compensation exists
                    anywhere. */}
                <div className="relative flex-1 min-h-0 flex flex-col">
                    <div
                        ref={setScrollRegion}
                        tabIndex={0}
                        role="region"
                        aria-label={`${label}, scrollable`}
                        className={cx(
                            'flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-hide focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400',
                            scrollClassName
                        )}
                    >
                        <table
                            ref={bodyTableRef}
                            aria-label={label}
                            style={TABLE_STYLE}
                            className={cx('w-full', isFixed && 'table-fixed')}
                        >
                            {isFixed && (
                                <colgroup>
                                    {shown.map((column) => (
                                        <col key={column.key} style={{ width: column.width }} />
                                    ))}
                                </colgroup>
                            )}
                            <thead>
                                <A11yHeaderRow columns={shown} sort={sort} />
                            </thead>
                            <tbody ref={refs?.body} className="bg-white">
                                {body}
                                {/* Inside the tbody so it scrolls with the rows and
                                inherits the table's column geometry. Only ever
                                after real rows - during the first load the
                                skeleton already says "wait". */}
                                {isInfinite && !isLoading && onLoadMore && rows.length > 0 && (
                                    <LoadMoreRow
                                        colSpan={shown.length}
                                        hasMore={hasMore}
                                        isLoadingMore={isLoadingMore}
                                        onLoadMore={onLoadMore}
                                        label={loadMoreLabel}
                                    />
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* The overlay thumb. Presentational only (aria-hidden):
                        keyboard scrolling happens natively through the
                        focusable region above. It stays mounted even while
                        invisible so the hook can size and place it without
                        re-running effects; without overflow it is inert and
                        unpainted. `pointer-events` only while shown, so a
                        hidden thumb never steals clicks from the row content
                        under the right edge. */}
                    <div
                        ref={overlay.thumbRef}
                        aria-hidden="true"
                        {...overlay.thumbHandlers}
                        className={cx(
                            SCROLLBAR_THUMB_BASE,
                            overlay.hasOverflow && overlay.isVisible
                                ? 'opacity-100'
                                : 'opacity-0 pointer-events-none'
                        )}
                    />
                </div>
                <div ref={refs?.pagination} className="shrink-0">
                    {footer}
                </div>
            </div>
        </>
    );

    if (expansion?.expanded) {
        return (
            <div
                ref={expansion.overlayRef}
                tabIndex={-1}
                role="region"
                aria-label={`${label}, expanded`}
                style={{ left: overlayLeft ?? DEFAULT_OVERLAY_LEFT }}
                className={cx(
                    'fixed inset-y-0 right-0 z-40 flex flex-col min-h-0 bg-gray-100 px-6 pb-6 focus:outline-none',
                    className
                )}
            >
                {content}
            </div>
        );
    }

    return <div className={cx('flex-1 min-h-0 flex flex-col', className)}>{content}</div>;
}

// Compound slots. Assigned as statements rather than exported consts so this
// file keeps exporting components only (react-refresh/only-export-components)
// and `DataTable`'s generic call signature survives untouched.
DataTable.Toolbar = DataTableToolbar;
DataTable.Search = DataTableSearch;
DataTable.ExpandToggle = DataTableExpandToggle;
DataTable.Pagination = DataTablePaginationBar;
DataTable.DataTableSortHeader = DataTableSortHeader;
DataTable.DataTableFilterDropdown = DataTableFilterDropdown;
DataTable.Filters = DataTableFilters;
DataTable.Actions = DataTableActions;
DataTable.ModeToggle = DataTableModeToggle;
DataTable.LoadMoreRow = LoadMoreRow;
